/**
 * StatusBadge — the small tinted pill a row carries for its state. The tint
 * carries the state and the text stays in the theme's own colour, which the
 * status cores are too light to replace. Given `onClick` it is a button, so a
 * state that has somewhere to go can take the reader there; its `title` is
 * then a tooltip.
 */
import React from 'react';

import { cn } from '@/lib/utils';
import { StatusTone } from '@/types/status';

import { Hint } from './Hint';

const TONE_CLASSES: Record<StatusTone, string> = {
    [StatusTone.Success]: 'bg-occ-green-200',
    [StatusTone.Warning]: 'bg-occ-yellow-200',
    [StatusTone.Danger]: 'bg-occ-red-200',
    [StatusTone.Neutral]: 'bg-occ-foreground-50',
    [StatusTone.Recording]: 'bg-occ-foreground-50',
};

// The hover steps the tint up, as the nav rail's badges do: the tint carries the state, so a
// foreground tint in its place would hide which state it is.
const HOVER_CLASSES: Record<StatusTone, string> = {
    [StatusTone.Success]: 'hover:bg-occ-green-400',
    [StatusTone.Warning]: 'hover:bg-occ-yellow-400',
    [StatusTone.Danger]: 'hover:bg-occ-red-400',
    [StatusTone.Neutral]: 'hover:bg-occ-foreground-100',
    [StatusTone.Recording]: 'hover:bg-occ-foreground-100',
};

const DOT_CLASSES: Record<StatusTone, string> = {
    [StatusTone.Success]: 'bg-occ-green-1000',
    [StatusTone.Warning]: 'bg-occ-yellow-1000',
    [StatusTone.Danger]: 'bg-occ-red-1000',
    [StatusTone.Neutral]: 'bg-occ-foreground-tertiary',
    [StatusTone.Recording]: 'bg-occ-red-1000',
};

interface StatusBadgeProps {
    tone: StatusTone;
    children: React.ReactNode;
    /** Leads the label with a dot, for a live state such as a tunnel. */
    hasDot?: boolean;
    title?: string;
    onClick?: () => void;
    className?: string;
}

export const StatusBadge = ({
    tone, children, hasDot = false, title, onClick, className,
}: StatusBadgeProps): React.JSX.Element => {
    const classes = cn(
        'inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded px-2 py-0.5',
        'text-xs font-medium text-occ-foreground-high-contrast',
        TONE_CLASSES[tone],
        onClick && HOVER_CLASSES[tone],
        className,
    );
    const content = (
        <>
            {hasDot && (
                <span className={cn('inline-block size-1.5 shrink-0 rounded-full', DOT_CLASSES[tone])} />
            )}
            <span className="truncate">{children}</span>
        </>
    );
    if (onClick) {
        return (
            <Hint label={title}>
                <button
                    type="button"
                    className={classes}
                    onClick={(event) => {
                        event.stopPropagation();
                        onClick();
                    }}
                >
                    {content}
                </button>
            </Hint>
        );
    }
    return (
        <span
            title={title}
            className={classes}
        >
            {content}
        </span>
    );
};
